/**
 * API client centralizado para o modulo WhatsApi.
 *
 * Tipos compartilhados + funcoes tipadas que consomem os endpoints
 * /api/v1/tenants/{id}/whatsapi/...
 *
 * O axios instance em ./client ja usa baseURL '/api', portanto as rotas
 * abaixo comecam em '/v1/...' (sem repetir o prefixo /api).
 */

import { api } from './client'

// ─── Tipos Compartilhados ────────────────────────────────────────────────────

export type WaMessageStatus = 'queued' | 'sent' | 'delivered' | 'read' | 'failed' | 'deleted'

export interface WaMessage {
  id: string
  api_key_id: string
  target: string
  message: string
  media_url: string | null
  message_id: string | null
  idempotency_key: string | null
  status: WaMessageStatus
  latency_ms: number | null
  created_at: string
  sent_at: string | null
  deleted_at: string | null
  delivered_at: string | null
  read_at: string | null
  resent_from_id: string | null
  source: string | null
  error: string | null
}

export interface WaMessageListParams {
  status?: WaMessageStatus
  api_key_id?: string
  target?: string
  source?: string
  from?: string
  to?: string
  page?: number
  page_size?: number
}

export interface WaMessageListResponse {
  items: WaMessage[]
  total: number
  page: number
  page_size: number
}

export interface WaSendPayload {
  target: string
  message: string
  api_key_id: string
  media_url?: string
  idempotency_key?: string
  source?: string
}

export interface WaApiKey {
  id: string
  name: string
  key_prefix: string
  is_active: boolean
  created_at: string
  last_used_at: string | null
}

export interface WaApiKeyWithSecret extends WaApiKey {
  key: string
}

export interface WaHealthResponse {
  status: 'ok' | 'degraded' | 'down'
  latency_ms: number | null
}

export interface WhatsApiConfig {
  configured: boolean
  updated_at?: string
  updated_by_email?: string
}

// ─── API Functions ───────────────────────────────────────────────────────────

const base = (tenantId: number) => `/v1/tenants/${tenantId}/whatsapi`

export const whatsapiApi = {
  listMessages: (tenantId: number, params?: WaMessageListParams): Promise<WaMessageListResponse> =>
    api.get(`${base(tenantId)}/messages`, { params }).then((r) => r.data),

  getMessage: (tenantId: number, id: string): Promise<WaMessage> =>
    api.get(`${base(tenantId)}/messages/${id}`).then((r) => r.data),

  sendMessage: (tenantId: number, payload: WaSendPayload): Promise<WaMessage> =>
    api.post(`${base(tenantId)}/messages`, payload).then((r) => r.data),

  resendMessage: (tenantId: number, id: string): Promise<WaMessage> =>
    api.post(`${base(tenantId)}/messages/${id}/resend`).then((r) => r.data),

  deleteMessage: (tenantId: number, id: string): Promise<WaMessage> =>
    api.post(`${base(tenantId)}/messages/${id}/delete`).then((r) => r.data),

  listApiKeys: (tenantId: number): Promise<WaApiKey[]> =>
    api.get(`${base(tenantId)}/api-keys`).then((r) => r.data.items),

  updateApiKey: (tenantId: number, id: string, is_active: boolean): Promise<WaApiKey> =>
    api.patch(`${base(tenantId)}/api-keys/${id}`, { is_active }).then((r) => r.data),

  deleteApiKey: (tenantId: number, id: string): Promise<void> =>
    api.delete(`${base(tenantId)}/api-keys/${id}`).then((r) => r.data),

  rotateApiKey: (tenantId: number, id: string): Promise<WaApiKeyWithSecret> =>
    api.post(`${base(tenantId)}/api-keys/${id}/rotate`).then((r) => r.data),

  getHealth: (tenantId: number): Promise<WaHealthResponse> =>
    api.get(`${base(tenantId)}/health`).then((r) => r.data),

  getConfig: (tenantId: number): Promise<WhatsApiConfig> =>
    api.get(`${base(tenantId)}/config`).then((r) => r.data),

  setConfig: (tenantId: number, masterKey: string): Promise<WhatsApiConfig> =>
    api.put(`${base(tenantId)}/config`, { master_key: masterKey }).then((r) => r.data),

  deleteConfig: (tenantId: number): Promise<void> =>
    api.delete(`${base(tenantId)}/config`).then(() => undefined),
}

// ─── KB (Base de Conhecimento) ───────────────────────────────────────────────

export type KbStatus = 'pending' | 'processing' | 'ready' | 'failed'
export type KbClassification = 'small' | 'large'

export interface KbFile {
  id: string
  filename: string
  content_type: string // tipo curto: md|txt|pdf|docx|csv|json
  size_bytes: number
  classification: KbClassification | null
  status: KbStatus
  error: string | null
  is_active: boolean
  created_at: string
  processed_at: string | null
}

export interface KbUploadResult {
  id: string
  status: KbStatus
  deduped: boolean
}

export interface KbContextSource {
  file_id: string
  filename: string
  kind: 'full' | 'chunk'
  seq?: number
  score?: number
}

export interface KbContextResult {
  api_key_id?: string
  context: string
  sources: KbContextSource[]
  version?: number | string
  truncated: boolean
  degraded: boolean
}

/** Raiz das rotas do WhatsApi do tenant; a da organização é `raizDaOrg` (`whatsapiOrg.ts`). */
export const raizDoTenant = base

/**
 * KB de uma chave. `raiz` é a raiz das rotas do WhatsApi do escopo (`raizDoTenant(id)` ou
 * `raizDaOrg(id)`): tenant e organização respondem na mesma forma.
 */
export const whatsapiKbApi = {
  list: (raiz: string, keyId: string): Promise<KbFile[]> =>
    api.get(`${raiz}/api-keys/${keyId}/kb`).then((r) => r.data.items),

  get: (raiz: string, keyId: string, fileId: string): Promise<KbFile> =>
    api.get(`${raiz}/api-keys/${keyId}/kb/files/${fileId}`).then((r) => r.data),

  upload: (raiz: string, keyId: string, file: File): Promise<KbUploadResult> => {
    const form = new FormData()
    form.append('file', file)
    return api
      .post(`${raiz}/api-keys/${keyId}/kb`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },

  setActive: (raiz: string, keyId: string, fileId: string, is_active: boolean): Promise<void> =>
    api
      .patch(`${raiz}/api-keys/${keyId}/kb/files/${fileId}`, { is_active })
      .then(() => undefined),

  remove: (raiz: string, keyId: string, fileId: string): Promise<void> =>
    api.delete(`${raiz}/api-keys/${keyId}/kb/files/${fileId}`).then(() => undefined),

  reprocess: (raiz: string, keyId: string, fileId: string): Promise<{ status: KbStatus }> =>
    api
      .post(`${raiz}/api-keys/${keyId}/kb/files/${fileId}/reprocess`)
      .then((r) => r.data),

  context: (raiz: string, keyId: string, q: string): Promise<KbContextResult> =>
    api
      .get(`${raiz}/api-keys/${keyId}/kb/context`, { params: { q } })
      .then((r) => r.data),
}

// ─── Admin API Functions ─────────────────────────────────────────────────────

export const whatsapiAdminApi = {
  toggleEnabled: (tenantId: number, enabled: boolean): Promise<void> =>
    api.put(`/v1/admin/tenants/${tenantId}/whatsapi/config`, { enabled }).then(() => undefined),
}
